import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Sparkles, Bookmark, BookmarkCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(false);
  const { user } = useAuth();

  const run = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    setSaved(false);
    try {
      const r = await api.post("/search", { query: q.trim() });
      setData(r.data);
    } catch { setData({ answer: "Search unavailable. Try again.", results: [] }); }
    finally { setBusy(false); }
  };

  const saveSearch = async () => {
    try {
      await api.post("/searches", { query: data.query || q.trim(), answer: data.answer, results: data.results || [] });
      setSaved(true);
      notify.success("Search saved to your profile.");
    } catch { notify.error("Could not save search."); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="p-2 rounded-sm hover:bg-slate-100" data-testid="global-search-trigger" aria-label="Search">
        <Search size={16} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" style={{ background: "rgba(15,23,42,0.65)" }} onClick={() => setOpen(false)} data-testid="global-search-modal">
          <div className="w-full max-w-2xl rounded-lg shadow-2xl" style={{ background: "var(--rp-card-bg)", border: "1px solid var(--rp-border)" }} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={run} className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
              <Search size={16} className="text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask anything · MAS TRM · PDPA breach · vendor DDQ…" className="flex-1 bg-transparent outline-none text-sm" data-testid="global-search-input" />
              <button type="button" onClick={() => setOpen(false)} className="p-1"><X size={16} /></button>
            </form>
            <div className="max-h-[60vh] overflow-y-auto">
              {!data && !busy && (
                <div className="p-6 text-sm text-slate-500">Hit Enter to search across <strong>32 frameworks</strong>, <strong>12 workflows</strong>, <strong>8 tools</strong> and <strong>300+ glossary terms</strong> — Claude-summarised.</div>
              )}
              {busy && <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2"><Sparkles size={14} className="animate-pulse" /> Asking Claude…</div>}
              {data && (
                <div className="p-4">
                  {data.answer && (
                    <div className="rp-card p-4 mb-3" data-testid="search-answer">
                      <div className="flex items-start justify-between gap-2">
                        <div className="overline-muted flex items-center gap-1"><Sparkles size={10} /> Claude</div>
                        {user && (
                          <button onClick={saveSearch} disabled={saved} className="text-[11px] inline-flex items-center gap-1 font-mono disabled:opacity-60" style={{ color: "var(--rp-accent)" }} data-testid="save-search-btn">
                            {saved ? <><BookmarkCheck size={11} /> Saved</> : <><Bookmark size={11} /> Save search</>}
                          </button>
                        )}
                      </div>
                      <p className="text-sm mt-2 leading-relaxed">{data.answer}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    {data.results?.map((r, i) => (
                      <Link key={i} to={r.url} onClick={() => setOpen(false)} className="block p-3 hover:bg-slate-50 rounded-sm border border-slate-100" data-testid={`search-result-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)" }}>{r.kind}</span>
                          <span className="font-display text-sm font-semibold">{r.title}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.summary}</p>
                      </Link>
                    ))}
                    {data.results?.length === 0 && <div className="text-sm text-slate-500 p-3">No direct matches — try a broader query.</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
